import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../../api/backendApi';

const STATUS_LABEL = {
  not_enabled: 'Not enabled',
  configuring: 'Configuring',
  configured: 'Configured',
  setup_incomplete: 'Setup incomplete',
  verification_failed: 'Verification failed',
};

function describedFailure(err) {
  const message = err?.message || 'SIP setup failed';
  const jsonStart = message.indexOf('{');
  if (jsonStart >= 0) {
    try {
      const parsed = JSON.parse(message.slice(jsonStart));
      return { message: parsed.error || 'SIP setup failed', data: parsed.data || null };
    } catch {
      return { message, data: null };
    }
  }
  return { message, data: err?.data || null };
}

async function readSetup(tenantId, path, method, body) {
  const response = await fetchWithAuth(`/api/v2/tenants/${tenantId}${path}`, method, body || null);
  if (!response?.success) {
    const error = new Error(response?.error || 'SIP setup failed');
    error.code = response?.code;
    error.data = response?.data;
    throw error;
  }
  return response.data;
}

const SipSetupPanel = ({ tenantId, canManage = false, canVerify = false }) => {
  const [setup, setSetup] = useState(null);
  const [domainName, setDomainName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const data = await readSetup(tenantId, '/sip-setup', 'GET');
    setSetup(data);
    if (!data.domainFqdn) setDomainName(data.suggestedDomain || '');
    return data;
  };

  useEffect(() => {
    if (!tenantId || !canVerify) return undefined;
    let cancel = false;
    load().catch((err) => {
      if (!cancel) setError(describedFailure(err).message);
    });
    return () => { cancel = true; };
  }, [tenantId, canVerify]);

  const run = async (path, body) => {
    setBusy(true);
    setError('');
    try {
      const data = await readSetup(tenantId, path, 'POST', body);
      setSetup(data);
      if (data.status === 'setup_incomplete') {
        toast.warn('Tenant created; SIP setup incomplete.');
      } else if (data.status === 'configured') {
        toast.success('SIP setup is configured. No phone registration or call was tested.');
      }
    } catch (err) {
      const failure = describedFailure(err);
      setError(failure.message);
      if (failure.data) setSetup(failure.data);
    } finally {
      setBusy(false);
    }
  };

  if (!canVerify || !setup) return null;
  const status = setup.status || 'not_enabled';
  const repairable = (setup.findings || []).some((item) => item.repairable);
  const established = Boolean(setup.domainFqdn);

  return (
    <section className="bg-white border border-gray-300 shadow-md rounded-lg p-6" data-testid="sip-setup-panel">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">SIP devices and paging</h2>
          <p className="mt-1 text-sm text-gray-600">
            Connect compatible SIP phones, speakers and paging endpoints. You can enable this later.
          </p>
        </div>
        <p className="text-sm font-medium text-gray-800" data-testid="sip-setup-status">
          {STATUS_LABEL[status] || status}
        </p>
      </div>
      {established ? (
        <p className="mt-3 text-sm text-gray-800">Domain <span className="font-mono">{setup.domainFqdn}</span></p>
      ) : canManage ? (
        <label className="mt-3 block text-sm text-gray-700">
          SIP domain
          <input
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
            value={domainName}
            onChange={(event) => setDomainName(event.target.value)}
            data-testid="sip-domain-name"
          />
        </label>
      ) : null}
      {setup.readinessNote ? <p className="mt-3 text-sm text-gray-600">{setup.readinessNote}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-700" data-testid="sip-setup-error">{error}</p> : null}
      {(setup.findings || []).length > 0 ? (
        <ul className="mt-3 list-disc pl-5 text-sm text-gray-700">
          {setup.findings.map((item) => <li key={item.code}>{item.message}</li>)}
        </ul>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {canManage && !established ? (
          <button type="button" className="rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-60" disabled={busy || !domainName} onClick={() => run('/sip-setup', { sipDomainName: domainName })}>
            Enable SIP
          </button>
        ) : null}
        {established ? (
          <button type="button" className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800" disabled={busy} onClick={async () => {
            setBusy(true);
            setError('');
            try {
              setSetup(await readSetup(tenantId, '/sip-setup/verify', 'GET'));
            } catch (err) {
              setError(describedFailure(err).message);
            } finally {
              setBusy(false);
            }
          }}>
            Verify SIP setup
          </button>
        ) : null}
        {canManage && established && (repairable || status === 'setup_incomplete' || status === 'verification_failed') ? (
          <button type="button" className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800" disabled={busy} onClick={() => run('/sip-setup/repair', {})}>
            Repair SIP setup
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default SipSetupPanel;
